/**
 * F 475 — THE CHAT ROUTING PILL.
 *
 * WHAT THIS IS
 * ------------
 * A tiny presentational component that says which model is thinking, while
 * a chat request is in flight.
 *
 * WHY IT IS ITS OWN COMPONENT
 * ---------------------------
 * The panel that hosts it (`AIChatPanel.tsx`) needs three context
 * providers — workspace, peek, and focus mode — so a test that renders
 * the whole panel has to stand up all three. The pill has no such need: it
 * takes a model name and renders it.
 *
 * Extracting it means the pill can be tested as a REAL COMPONENT with a
 * REAL render, and the panel's own test does not have to fake a provider
 * stack to prove that a string appears on screen.
 *
 * WHY IT IS NOT A PERMANENT STATUS LINE
 * -------------------------------------
 * A pill that is always on screen is decoration. A pill that says
 * "Thinking" when nothing is thinking is a lie. The panel therefore
 * renders it ONLY while `busy` is true, and this component's contract is
 * simply "given a model name, say which model it is".
 */
import React from 'react';
import { Box, Group, Text } from '@mantine/core';
import { IconRobot } from '@tabler/icons-react';

export interface RoutingPillProps {
  /** The model that is handling the request. */
  model: string;
  /** The provider serving it, shown as secondary context. */
  provider?: string;
}

/**
 * The pill's text. Exported so a test can assert the exact wording
 * without reaching into the rendered DOM for a substring.
 */
export function routingPillText(model: string): string {
  return `Thinking via ${model}…`;
}

export const RoutingPill: React.FC<RoutingPillProps> = ({ model, provider }) => (
  <Group
    gap={6}
    wrap="nowrap"
    align="center"
    data-testid="fc-ai-routing-pill"
    data-model={model}
    data-provider={provider}
    style={{ minWidth: 0 }}
  >
    <IconRobot size={14} style={{ flexShrink: 0 }} />
    <Text size="xs" c="dimmed" truncate style={{ minWidth: 0 }}>
      {routingPillText(model)}
    </Text>
  </Group>
);

export default RoutingPill;
